import {
    createSlice,
    createSelector,
    createAsyncThunk,
    PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from '../store';
import { LoadingStatus } from '../types';
import { auth } from '../../Lib/firebase';
import { getDeviceInfo, getDeviceToken } from '../../Utils/helpers';
import { signInWithEmailAndPassword, UserCredential } from 'firebase/auth';
import api from 'src/modules/API';
import { isProd } from 'src/modules/Lib/config';

interface AuthState {
    deviceId: string | null;
    deviceHash: string | null;
    autoSignInSucceeded: boolean;
    status: LoadingStatus;
}

const initialState = {
    deviceId: null,
    deviceHash: null,
    autoSignInSucceeded: false,
    status: LoadingStatus.Idle,
} as AuthState;

const signIn = async (deviceId?: string | null, hash?: string | null) => {
    const deviceToken = getDeviceToken();
    !isProd && console.log('[signIn]', { deviceToken, deviceId, hash });
    if (deviceToken) {
        return await api.devices.getThis();
    }

    if (!deviceId || !hash) return;

    const email = `${deviceId}@snackpass.co`;
    !isProd && console.log('[signIn]', { email });
    try {
        const result = await signInWithEmailAndPassword(auth, email, hash);

        !isProd && console.log('[signIn]', { email, result });
        return result;
    } catch (err) {
        !isProd && console.log('[signIn]', { email, err });
    }
};

export const attemptSignIn = createAsyncThunk(
    'auth/attemptSignIn',
    async (_, { dispatch }) => {
        const { deviceId, hash } = getDeviceInfo();

        dispatch(setDeviceId(deviceId));
        dispatch(setDeviceHash(hash));

        return await signIn(deviceId, hash);
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setDeviceId(state: AuthState, action: PayloadAction<string>) {
            state.deviceId = action.payload;
        },
        setDeviceHash(state: AuthState, action: PayloadAction<string>) {
            state.deviceHash = action.payload;
        },
        setAutoSignInSucceeded(
            state: AuthState,
            action: PayloadAction<boolean>
        ) {
            state.autoSignInSucceeded = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(attemptSignIn.pending, (state: AuthState) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(
                attemptSignIn.fulfilled,
                (
                    state: AuthState,
                    action: PayloadAction<
                        { device: any; user: any } | UserCredential | undefined
                    >
                ) => {
                    state.autoSignInSucceeded = action.payload !== undefined;
                    state.status = LoadingStatus.Success;
                }
            )
            .addCase(attemptSignIn.rejected, (state: AuthState) => {
                state.status = LoadingStatus.Failed;
            });
    },
});

export const getAuthState = (state: RootState) => state.auth;
export const getDeviceId = createSelector(
    [getAuthState],
    auth => auth.deviceId
);
export const getAutoSignInSucceeded = createSelector(
    [getAuthState],
    auth => auth.autoSignInSucceeded
);

export const { setDeviceId, setDeviceHash, setAutoSignInSucceeded } =
    authSlice.actions;
export const authReducer = authSlice.reducer;
